// ═══════════════════════════════════════════════════════════════════════════════
// UNIFIED PERMISSIONS SYSTEM
// This single file contains ALL permission-related logic:
// - Constants (MODULES, ACTIONS, STORAGE_KEYS, USER_TYPES)
// - Routes configuration (for sidebar, route guards, first available route)
// - Core permission functions
// - Navigation helpers (sidebar menu, first available route, route access)
// - React hooks (usePermissions + all shortcut hooks)
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1: CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  PERMISSIONS: 'permissions',
  SESSION_TOKEN: 'sessionToken',
  PENDING_USER: 'pendingUser',
};

export const USER_TYPES = {
  SUPER_ADMIN: 'super-admin',
  ADMIN: 'admin',
  ORGANIZATION_ADMIN: 'organization-admin',
  ORG_USER: 'org-user',
};

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  ALL: '*', // Wildcard for all permissions (super-admin)
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2: MODULES (All module slugs in one place)
// ═══════════════════════════════════════════════════════════════════════════════

export const MODULES = {
  DASHBOARD: 'dashboard',
  PROJECTS: 'projects',
  INSIGHTS_ENGINE: 'insights-engine',
  SURVEY_BUILDER: 'survey-builder',
  AI_JDV: 'ai-jdv',
  CAMPAIGN_MANAGEMENT: 'campaign-management',
  USER_MANAGEMENT: 'user-management',
  ROLE_MANAGEMENT: 'role-management',
  MODULE_MANAGEMENT: 'module-management',
  ORGANIZATION_MANAGEMENT: 'organization-management',
  GENZ_SIGNALS: 'genz-signals',
  GENZ_DASHBOARD: 'gen-z-dashboard',
  VITALCITY_DASHBOARD: 'vital-city-dashboard',
};

export const VITALCITY_ORG_ID = '692e99aafe70445feef6c861';

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3: ROUTES CONFIGURATION
// Single source of truth for:
// - Sidebar menu items
// - Route permission guards
// - First available route calculation
// ═══════════════════════════════════════════════════════════════════════════════

export const ROUTES = [
  {
    key: 'dashboard',
    path: '/',
    module: MODULES.DASHBOARD,
    label: 'Dashboard',
    icon: '/images/home-icon.svg',
    sidebar: true,
    order: 1,
    superAdminOnly: true, // user monitoring tool — super-admin only
  },
  {
    key: 'genz-signals',
    path: '/genz-signals/',
    module: MODULES.GENZ_SIGNALS,
    label: 'Gen Z Signal',
    icon: '/images/genz-signals-icon.svg',
    sidebar: true,
    order: 2,
  },
  {
    key: 'genz-dashboard',
    path: '/gen-z-dashboard/',
    module: MODULES.GENZ_DASHBOARD,
    label: 'Home',
    icon: '/images/home-icon.svg',
    sidebar: true,
    order: 1.5,
  },
  {
    key: 'vitalcity-dashboard',
    path: '/vitalcity/',
    module: MODULES.VITALCITY_DASHBOARD,
    label: 'Vital City NYC',
    icon: '/images/home-icon.svg',
    sidebar: true,
    order: 1.6,
  },
  {
    key: 'projects',
    path: '/projects/',
    module: MODULES.PROJECTS,
    label: 'Library',
    description: 'Store and manage transcripts',
    icon: '/images/projects-icon.svg',
    sidebar: true,
    order: 3,
  },
  {
    key: 'insights',
    path: '/insight-engine/',
    module: MODULES.INSIGHTS_ENGINE,
    label: 'Analyze',
    description: 'Produce reports and ask questions',
    icon: '/images/system-icon.svg',
    sidebar: true,
    order: 3,
  },
  {
    key: 'survey',
    path: '/surveys/',
    module: MODULES.SURVEY_BUILDER,
    label: 'Survey Builder',
    icon: '/images/surveys-icon.svg',
    sidebar: true,
    order: 4,
  },
  {
    key: 'aijdv',
    path: '/polling-agents/',
    module: MODULES.AI_JDV,
    label: 'Design',
    description: 'Write questions and guides',
    icon: '/images/matrix-icon.svg',
    sidebar: true,
    order: 5,
  },
  {
    key: 'campaigns',
    path: '/campaigns/',
    module: MODULES.CAMPAIGN_MANAGEMENT,
    label: 'Launch',
    description: 'Create Studies, track progress and completes',
    icon: '/images/study-icon.svg',
    sidebar: true,
    order: 6,
  },
  {
    key: 'manage',
    path: '/manage',
    // Multiple modules - user sees this if they have access to ANY of these
    modules: [MODULES.USER_MANAGEMENT, MODULES.ROLE_MANAGEMENT, MODULES.MODULE_MANAGEMENT],
    label: 'User & Role Management',
    icon: '/images/user-role-icon.svg',
    sidebar: true,
    order: 8,
  },
  {
    key: 'organizations',
    path: '/organizations',
    module: MODULES.ORGANIZATION_MANAGEMENT,
    label: 'Organization',
    icon: '/images/organization-icon.svg',
    sidebar: true,
    order: 9,
  },
  {
    key: 'user-tracking',
    path: '/user-tracking',
    label: 'User Tracking',
    icon: '/images/user-role-icon.svg',
    sidebar: true,
    order: 10,
    superAdminOnly: true, // billing users table - super-admin only
  },
  {
    key: 'monitoring-user',
    path: '/monitoring/users',
    label: 'User Monitor',
    sidebar: false,
    order: 99,
    superAdminOnly: true, // drill-in page from Dashboard; not shown in sidebar
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4: CORE UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get user data from localStorage
 */
export const getUserData = () => {
  try {
    const userDataStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userDataStr ? JSON.parse(userDataStr) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Get user type from localStorage
 */
export const getUserType = () => {
  const userData = getUserData();
  return userData?.user_type || null;
};

/**
 * Get user ID
 */
export const getUserId = () => {
  const userData = getUserData();
  return userData?._id || userData?.id || null;
};

/**
 * Get the current user's organization id (string), regardless of whether
 * the stored user object holds the org as a populated document, an ObjectId,
 * or a flat `organization_id` field.
 */
export const getUserOrganizationId = () => {
  const userData = getUserData();
  if (!userData) return null;
  // Populated org doc: { _id, name, ... }
  if (userData.organization?._id) return String(userData.organization._id);
  // Raw ObjectId or string
  if (userData.organization && typeof userData.organization !== 'object') {
    return String(userData.organization);
  }
  // Flat field set by some flows
  if (userData.organization_id) return String(userData.organization_id);
  return null;
};

/**
 * True when the current user is an organization-admin AND the given entity
 * (project or chat) belongs to the same organization. Used by detail pages
 * to grant full management UI to peer org-admins, mirroring the backend
 * which already permits same-org-admins to view/manage org data.
 *
 * @param {string|object|null} entityOrganization - The entity's `organization`
 *   field. Accepts a populated org document, an ObjectId/string id, or null.
 * @returns {boolean}
 */
export const isOrgAdminInSameOrg = (entityOrganization) => {
  if (getUserType() !== USER_TYPES.ORGANIZATION_ADMIN) return false;
  const userOrgId = getUserOrganizationId();
  if (!userOrgId || !entityOrganization) return false;
  const entityOrgId = entityOrganization?._id
    ? String(entityOrganization._id)
    : String(entityOrganization);
  return userOrgId === entityOrgId;
};

/**
 * Get user name (title case)
 */
export const getUserName = () => {
  const userData = getUserData();
  const name = userData?.name || userData?.username || '';
  return toTitleCase(name);
};

/**
 * Check if current user is super-admin (has ALL permissions)
 */
export const isSuperAdmin = () => {
  return getUserType() === USER_TYPES.SUPER_ADMIN;
};

/**
 * Check if current user is super-admin or admin (system-level users)
 */
export const isSystemAdmin = () => {
  const userType = getUserType();
  return userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ADMIN;
};

/**
 * Get all permissions from localStorage
 */
export const getPermissions = () => {
  try {
    const permissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    return permissions ? JSON.parse(permissions) : [];
  } catch (e) {
    return [];
  }
};

/**
 * Check if user is logged in
 */
export const isLogin = () => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return !!token;
};

/**
 * Get user access token
 */
export const getUserToken = () => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || null;
};

/**
 * Get refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || null;
};

/**
 * Clear all localStorage
 */
export const clearStorage = () => {
  localStorage.clear();
};

/**
 * Convert string to title case
 */
export const toTitleCase = (str) => {
  if (!str) return str;
  str = str.replace(/_/g, ' ');
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5: MODULE PERMISSION FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check module-level permissions
 * @param {string} moduleSlug - The module slug to check
 * @returns {Object} - { authorized, message, sub_modules }
 */
export const checkModulePermissions = (moduleSlug) => {
  const userData = getUserData();

  if (!userData) {
    return {
      authorized: false,
      message: 'Not Authorized',
      sub_modules: [],
    };
  }

  const userType = userData?.user_type || '';

  // Super-admin bypass - has all permissions
  if (userType === USER_TYPES.SUPER_ADMIN) {
    return {
      authorized: true,
      message: 'Authorized (Super Admin)',
      sub_modules: [ACTIONS.ALL],
    };
  }

  const permissions = getPermissions();
  const permissionResponse = {
    authorized: false,
    message: 'Not Authorized',
    sub_modules: [],
  };

  const modulePermission = permissions.find(
    (permission) => permission.module?.module_slug === moduleSlug
  );

  if (modulePermission) {
    const subModules = modulePermission.sub_modules?.map(
      (subModule) => subModule.sub_module_slug
    ) || [];

    permissionResponse.authorized = true;
    permissionResponse.message = 'Authorized';
    permissionResponse.sub_modules = subModules;
  }

  return permissionResponse;
};

/**
 * Check if user has access to a module
 * @param {string} moduleSlug - The module slug to check
 * @returns {boolean}
 */
export const hasModuleAccess = (moduleSlug) => {
  return checkModulePermissions(moduleSlug).authorized;
};

/**
 * Check if user has access to a specific sub-module/action
 * Supports multiple slug formats:
 * - Generic: 'create', 'edit', 'delete', 'view'
 * - Module-specific: 'create-campaigns', 'edit-users', etc.
 * - Custom actions: 'publish', 'launch', 'sync', 'activate', etc.
 *
 * @param {string} moduleSlug - The module slug (e.g., 'campaign-management')
 * @param {string} action - The action slug (e.g., 'create', 'edit', 'publish')
 * @returns {boolean}
 */
export const hasSubModuleAccess = (moduleSlug, action) => {
  const modulePermission = checkModulePermissions(moduleSlug);
  if (!modulePermission.authorized) return false;

  const subModules = modulePermission.sub_modules;

  // Check for wildcard (*) - super-admin or full access
  if (subModules.includes(ACTIONS.ALL)) return true;

  // Check for exact match (generic slug like 'create', 'edit')
  if (subModules.includes(action)) return true;

  // Check for module-specific slug patterns:
  // 'create' should match 'create-campaigns', 'create-users', etc.
  const moduleNameVariants = getModuleNameVariants(moduleSlug);

  for (const variant of moduleNameVariants) {
    // Check patterns like: create-campaigns, edit-users, delete-roles
    if (subModules.includes(`${action}-${variant}`)) return true;
    // Check patterns like: view-campaign, create-user (singular)
    if (subModules.includes(`${action}-${variant.replace(/s$/, '')}`)) return true;
  }

  return false;
};

/**
 * Get possible name variants from a module slug
 * e.g., 'campaign-management' -> ['campaigns', 'campaign']
 * e.g., 'user-management' -> ['users', 'user']
 */
const getModuleNameVariants = (moduleSlug) => {
  const variants = [];

  // Module slug to possible sub-module name mappings
  const moduleNameMap = {
    'campaign-management': ['campaigns', 'campaign'],
    'user-management': ['users', 'user'],
    'role-management': ['roles', 'role'],
    'module-management': ['modules', 'module'],
    'organization-management': ['organizations', 'organization', 'orgs', 'org'],
    'survey-builder': ['surveys', 'survey'],
    'ai-jdv': ['polling-agents', 'agents', 'agent', 'ai-jdv'],
    'projects': ['projects', 'project'],
    'insights-engine': ['insights', 'insight'],
    'dashboard': ['dashboard'],
  };

  if (moduleNameMap[moduleSlug]) {
    variants.push(...moduleNameMap[moduleSlug]);
  }

  // Also try extracting from slug itself (e.g., 'campaign-management' -> 'campaign')
  const parts = moduleSlug.split('-');
  if (parts.length > 0) {
    variants.push(parts[0]);
    variants.push(parts[0] + 's'); // plural
  }

  return [...new Set(variants)]; // Remove duplicates
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 6: ACTION PERMISSION FUNCTIONS (Convenience shortcuts)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user can perform a specific action on a module
 */
export const canPerformAction = (moduleSlug, action) => {
  return hasSubModuleAccess(moduleSlug, action);
};

/**
 * Check if user can view a module
 */
export const canView = (moduleSlug) => canPerformAction(moduleSlug, ACTIONS.VIEW);

/**
 * Check if user can create in a module
 */
export const canCreate = (moduleSlug) => canPerformAction(moduleSlug, ACTIONS.CREATE);

/**
 * Check if user can edit in a module
 */
export const canEdit = (moduleSlug) => canPerformAction(moduleSlug, ACTIONS.EDIT);

/**
 * Check if user can delete in a module
 */
export const canDelete = (moduleSlug) => canPerformAction(moduleSlug, ACTIONS.DELETE);

/**
 * Get all CRUD permissions for a module in one call
 * @param {string} moduleSlug - The module slug
 * @returns {Object} - { authorized, canView, canCreate, canEdit, canDelete, sub_modules }
 */
export const getModulePermissions = (moduleSlug) => {
  const modulePermission = checkModulePermissions(moduleSlug);

  return {
    authorized: modulePermission.authorized,
    sub_modules: modulePermission.sub_modules,
    canView: hasSubModuleAccess(moduleSlug, ACTIONS.VIEW),
    canCreate: hasSubModuleAccess(moduleSlug, ACTIONS.CREATE),
    canEdit: hasSubModuleAccess(moduleSlug, ACTIONS.EDIT),
    canDelete: hasSubModuleAccess(moduleSlug, ACTIONS.DELETE),
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7: SIDEBAR & NAVIGATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get sidebar menu items (filtered by permission)
 * Used by Sidebar.js
 */
export const getSidebarMenu = () => {
  // Super-admin sees everything
  if (isSuperAdmin()) {
    return ROUTES.filter((r) => r.sidebar).sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  const userOrgId = getUserOrganizationId();

  return ROUTES
    .filter((route) => {
      if (!route.sidebar) return false;

      // Super-admin-only routes (e.g. Dashboard, User Monitor) are hidden for everyone else
      if (route.superAdminOnly) return false;

      // Org-scoped routes (e.g. VitalCity dashboard): if the user's org is
      // in the allowlist, they see the entry regardless of module config
      // (the route IS the org-scoped permission). If the user's org is not
      // in the allowlist, the entry is hidden.
      if (Array.isArray(route.orgIds) && route.orgIds.length > 0) {
        if (userOrgId && route.orgIds.includes(userOrgId)) return true;
        return false;
      }

      // Multi-module route (like /manage) - show if ANY module is accessible
      if (route.modules) {
        return route.modules.some((m) => hasModuleAccess(m));
      }

      // Single module route
      return hasModuleAccess(route.module);
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Get first available route (for redirect after login or when accessing forbidden page)
 * Used by PrivateRoute.js and login redirect
 */
export const getFirstAvailableRoute = () => {
  const menu = getSidebarMenu();
  return menu.length > 0 ? menu[0] : null;
};

/**
 * Check if user can access a specific route path
 * Used by PrivateRoute.js
 */
export const canAccessRoute = (pathname) => {
  // Super-admin can access everything
  if (isSuperAdmin()) return true;

  // Find matching route from config
  const route = ROUTES.find((r) => {
    // Exact match
    if (pathname === r.path) return true;
    // Prefix match (e.g., /projects/123 matches /projects/)
    if (r.path !== '/' && pathname.startsWith(r.path)) return true;
    return false;
  });

  // No matching route config = allow (it's either a public route or not protected)
  if (!route) return true;

  // Super-admin-only routes lock out everyone else
  if (route.superAdminOnly) return false;

  // Org-scoped routes: if the user's org is listed, they're allowed in
  // (the route IS the org-scoped permission, no module record required).
  // If their org is not listed, they're blocked.
  if (Array.isArray(route.orgIds) && route.orgIds.length > 0) {
    const userOrgId = getUserOrganizationId();
    if (userOrgId && route.orgIds.includes(userOrgId)) return true;
    return false;
  }

  // Multi-module route (like /manage) - allow if ANY module is accessible
  if (route.modules) {
    return route.modules.some((m) => hasModuleAccess(m));
  }

  // Single module route
  return hasModuleAccess(route.module);
};

/**
 * Get route config by path
 */
export const getRouteByPath = (pathname) => {
  return ROUTES.find((r) => {
    if (pathname === r.path) return true;
    if (r.path !== '/' && pathname.startsWith(r.path)) return true;
    return false;
  });
};

/**
 * Get route config by key
 */
export const getRouteByKey = (key) => {
  return ROUTES.find((r) => r.key === key);
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 8: REACT HOOKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook to get current user info
 * Returns userId, userType, and other user details
 */
export const useUserInfo = () => {
  const userData = useMemo(() => getUserData(), []);
  const userId = useMemo(() => getUserId(), []);
  const userType = useMemo(() => getUserType(), []);

  return {
    userId,
    userType,
    userData,
    isSuperAdmin: userType === USER_TYPES.SUPER_ADMIN,
    isAdmin: userType === USER_TYPES.ADMIN,
    isSystemAdmin: userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.ADMIN,
    isOrgAdmin: userType === USER_TYPES.ORGANIZATION_ADMIN,
    isOrgUser: userType === USER_TYPES.ORG_USER,
  };
};

/**
 * Main permission hook
 * 
 * Usage:
 * const { can, permissions } = usePermissions('projects');
 * 
 * // Check module access
 * if (permissions.authorized) { ... }
 * 
 * // Check specific actions
 * if (can('create')) { ... }
 * if (can('edit')) { ... }
 * if (can('delete')) { ... }
 * if (can('share')) { ... }  // custom actions work too
 * 
 * // Or without module (for checking multiple modules):
 * const { hasAccess, canPerform } = usePermissions();
 * if (hasAccess('campaign-management')) { ... }
 * if (canPerform('campaign-management', 'delete')) { ... }
 */
export const usePermissions = (moduleSlug = null) => {
  // Get user info
  const userData = useMemo(() => getUserData(), []);
  const userType = useMemo(() => getUserType(), []);
  const isSuperAdminUser = useMemo(() => isSuperAdmin(), []);
  const isSystemAdminUser = useMemo(() => isSystemAdmin(), []);

  // Get module-specific permissions if moduleSlug provided
  const permissions = useMemo(() => {
    if (!moduleSlug) return null;
    return getModulePermissions(moduleSlug);
  }, [moduleSlug]);

  // Shorthand for checking action within the specified module
  const can = useCallback((action) => {
    if (!moduleSlug) return false;
    return hasSubModuleAccess(moduleSlug, action);
  }, [moduleSlug]);

  // Check access to any module
  const hasAccess = useCallback((slug) => {
    return hasModuleAccess(slug);
  }, []);

  // Check action on any module
  const canPerform = useCallback((slug, action) => {
    return hasSubModuleAccess(slug, action);
  }, []);

  // Get full permissions for any module
  const getPerms = useCallback((slug) => {
    return getModulePermissions(slug);
  }, []);

  return {
    // User info
    userData,
    userType,
    isSuperAdmin: isSuperAdminUser,
    isSystemAdmin: isSystemAdminUser,

    // Module-specific (when moduleSlug provided)
    permissions,
    can,

    // Generic utilities (work with any module)
    hasAccess,
    canPerform,
    getPerms,

    // Direct access to utility functions
    checkModule: checkModulePermissions,
    canView: (slug) => canView(slug || moduleSlug),
    canCreate: (slug) => canCreate(slug || moduleSlug),
    canEdit: (slug) => canEdit(slug || moduleSlug),
    canDelete: (slug) => canDelete(slug || moduleSlug),

    // Constants for convenience
    ACTIONS,
    MODULES,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 9: SHORTCUT HOOKS
// Pre-configured hooks so you don't need to remember module slugs
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Dashboard permissions
 * Usage: const { can } = useDashboardPermissions();
 */
export const useDashboardPermissions = () => usePermissions(MODULES.DASHBOARD);

/**
 * Projects permissions
 * Usage: const { can } = useProjectPermissions();
 *        {can('create') && <Button>Create</Button>}
 *        {can('share') && <Button>Share</Button>}
 *        {can('upload-files') && <Button>Upload</Button>}
 */
export const useProjectPermissions = () => usePermissions(MODULES.PROJECTS);

/**
 * Insights Engine permissions
 * Usage: const { can } = useInsightsPermissions();
 */
export const useInsightsPermissions = () => usePermissions(MODULES.INSIGHTS_ENGINE);

/**
 * Survey Builder permissions
 * Usage: const { can } = useSurveyPermissions();
 *        {can('create') && <Button>Create</Button>}
 *        {can('save') && <Button>Save</Button>}
 *        {can('regenerate') && <Button>Regenerate</Button>}
 */
export const useSurveyPermissions = () => usePermissions(MODULES.SURVEY_BUILDER);

/**
 * Interview Agent (Polling Agents) permissions
 * Usage: const { can } = usePollingAgentPermissions();
 *        {can('create') && <Button>Create</Button>}
 *        {can('activate') && <Button>Activate</Button>}
 */
export const usePollingAgentPermissions = () => usePermissions(MODULES.AI_JDV);

/**
 * Campaign Management permissions
 * Usage: const { can } = useCampaignPermissions();
 *        {can('create') && <Button>Create</Button>}
 *        {can('publish') && <Button>Publish</Button>}
 *        {can('launch') && <Button>Launch</Button>}
 */
export const useCampaignPermissions = () => usePermissions(MODULES.CAMPAIGN_MANAGEMENT);

/**
 * User Management permissions
 * Usage: const { can } = useUserPermissions();
 */
export const useUserPermissions = () => usePermissions(MODULES.USER_MANAGEMENT);

/**
 * Role Management permissions
 * Usage: const { can } = useRolePermissions();
 */
export const useRolePermissions = () => usePermissions(MODULES.ROLE_MANAGEMENT);

/**
 * Module Management permissions
 * Usage: const { can } = useModulePermissions();
 */
export const useModulePermissions = () => usePermissions(MODULES.MODULE_MANAGEMENT);

/**
 * Organization Management permissions
 * Usage: const { can } = useOrganizationPermissions();
 */
export const useOrganizationPermissions = () => usePermissions(MODULES.ORGANIZATION_MANAGEMENT);

/**
 * Gen Z Signal permissions
 * Usage: const { can } = useGenZSignalsPermissions();
 *        {can('send-message') && <Button>Send</Button>}
 *        {can('share') && <Button>Share</Button>}
 *        {can('analytics') && <Button>Analytics</Button>}
 *        {can('build-prompt') && <Button>Build Prompt</Button>}
 *        {can('suggested-prompts') && <SuggestedPrompts />}
 *        {can('prompt-library') && <PromptLibrary />}
 *        {can('chat-history') && <ChatHistory />}
 *        {can('create') && <Button>New Chat</Button>}
 */
export const useGenZSignalsPermissions = () => usePermissions(MODULES.GENZ_SIGNALS);

/**
 * Gen Z Dashboard permissions
 * Usage: const { can } = useGenZDashboardPermissions();
 *        {can('in-depth-analysis') && <Tab>In-depth Analysis</Tab>}
 */
export const useGenZDashboardPermissions = () => usePermissions(MODULES.GENZ_DASHBOARD);

/**
 * VitalCity dashboard permissions.
 * Visibility is also gated by org id (see ROUTES entry) — only super-admins
 * and members of the VitalCity organization see this dashboard.
 */
export const useVitalCityDashboardPermissions = () => usePermissions(MODULES.VITALCITY_DASHBOARD);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 10: DEFAULT EXPORT (for backward compatibility)
// ═══════════════════════════════════════════════════════════════════════════════

const permissionsUtils = {
  // Constants
  STORAGE_KEYS,
  USER_TYPES,
  ACTIONS,
  MODULES,
  ROUTES,
  // Core utilities
  getUserData,
  getUserType,
  getUserId,
  getUserName,
  getUserOrganizationId,
  isOrgAdminInSameOrg,
  isSystemAdmin,
  isSuperAdmin,
  getPermissions,
  isLogin,
  getUserToken,
  getRefreshToken,
  clearStorage,
  toTitleCase,
  // Module checks
  checkModulePermissions,
  hasModuleAccess,
  hasSubModuleAccess,
  // Action checks
  canPerformAction,
  canView,
  canCreate,
  canEdit,
  canDelete,
  getModulePermissions,
  // Navigation
  getSidebarMenu,
  getFirstAvailableRoute,
  canAccessRoute,
  getRouteByPath,
  getRouteByKey,
  // Hooks
  usePermissions,
  useDashboardPermissions,
  useProjectPermissions,
  useInsightsPermissions,
  useSurveyPermissions,
  usePollingAgentPermissions,
  useCampaignPermissions,
  useUserPermissions,
  useRolePermissions,
  useModulePermissions,
  useOrganizationPermissions,
};

export default permissionsUtils;
